import React, { useContext } from 'react'
import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom'
import { AuthContext, AuthRoutes } from '@dexter-experts/auth-react'
import {
    ApolloProvider,
    ApolloClient,
    InMemoryCache,
    from,
    HttpLink,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import cleanTypenameLink from './utils/cleanTypenameLink'
import AppHeader from './components/AppHeader'
import SingleProject from './views/SingleProject'
import Projects from './views/Projects'
import NewProject from './views/NewProject'
import Payment from './views/Payments'
import Experts from './views/Experts'
import Admin from './views/Admin'
import FirstTimeSignupPage from './views/FirstTimeSignupPage'
import Landing from './views/Landing'
import NoMatch from './components/NotFoundPage'
import DataContext, { DataProvider } from './contexts/DataContext'

const AdminRoute = ({ ...props }) => {
    const { user } = useContext(DataContext)
    if (!user.isSuperUser()) return <Redirect to="/" />
    return <Route {...props} />
}

const guestUserPages = (
    <Switch>
        <Route path="/" component={Landing} />
    </Switch>
)

const LoggedInPages = () => {
    const { user } = useContext(DataContext)
    const isPortalUser = user.isDecibio() || user.isSuperUser()

    if (!isPortalUser) {
        return (
            <Switch>
                <Route
                    exact
                    path="/first-time-signup-notice"
                    component={FirstTimeSignupPage}
                />
                <Route render={() => <Redirect to="/first-time-signup-notice" />} />
            </Switch>
        )
    }

    return (
        <>
            <AppHeader />
            <Switch>
                <Route exact path="/" render={() => <Redirect to="/projects" />} />
                <Route exact path="/payments" component={Payment} />
                <Route exact path="/projects" component={Projects} />
                <Route exact path="/projects/new-project" component={NewProject} />
                <Route exact path="/projects/:type" component={Projects} />
                <Route exact path="/projects/:id" component={SingleProject} />
                <Route exact path="/projects/:id/:page" component={SingleProject} />
                <AdminRoute exact path="/admin" component={Admin} />
                <AdminRoute path="/admin/team/:id/:type" component={Admin} />
                <AdminRoute path="/admin/:page" component={Admin} />
                <Route exact path="/experts" component={Experts} />
                <Route
                    exact
                    path="/first-time-signup-notice"
                    component={FirstTimeSignupPage}
                />
                <Route exact path="/not-found-page" component={NoMatch} />
                <Route component={NoMatch} />
            </Switch>
        </>
    )
}

const loggedInUserPages = (
    <DataProvider>
        <LoggedInPages />
    </DataProvider>
)

const Routes = () => {
    const { token, authUser } = useContext(AuthContext)
    const isLoggedIn = Boolean(token && authUser?.emailVerified)
    const authLink = setContext(async (_, { headers }) => ({
        headers: { ...headers, ...(token && { authorization: `Bearer ${token}` }) },
    }))
    const httpLink = new HttpLink({
        uri: process.env.REACT_APP_GRAPHQL_BASE_URL,
    })

    const apolloClient = new ApolloClient({
        link: from([authLink, cleanTypenameLink, httpLink]),
        cache: new InMemoryCache({
            typePolicies: {
                Project: {
                    merge: true,
                    fields: {
                        calendar: { merge: false },
                    },
                },
            },
        }),
    })

    const getPages = () => {
        if (isLoggedIn) {
            return loggedInUserPages
        }
        return guestUserPages
    }

    return (
        <ApolloProvider client={apolloClient}>
            <BrowserRouter>
                <Switch>
                    <AuthRoutes />
                    <Route
                        render={() => (
                            <div className="site-wrap">
                                <div className={!isLoggedIn ? 'fullscreen' : 'main'}>
                                    {getPages()}
                                </div>
                            </div>
                        )}
                    />
                </Switch>
            </BrowserRouter>
        </ApolloProvider>
    )
}

export default Routes
